'use client';

import { PropsWithChildren, useState } from 'react';
import Link from 'next/link';
import DallasStack from '~/app/_ui/Dallas/Stack/DallasStack';
import styles from './ResponsiveNavigationLayout.module.scss';
import DallasImageTruckSmarterLogo from '@ui/Dallas/Images/TruckSmarterLogo/DallasImageTruckSmarterLogo';
import NavIconButton from '../NavItems/NavIconButton';
import DallasSvgIconMenuOutlined from '@ui/Dallas/Icons/DallasSvgIconMenuOutlined';
import DallasSvgIconMenuOpenOutlined from '@ui/Dallas/Icons/DallasSvgIconMenuOpenOutlined';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import useDidChange from '@ui/useDidChange';
import { useViewportWidthClassType } from '@ui/shared/ViewportSizeProvider/ViewportSizeProvider';
import { Dialog as BaseOverlay, useDialogStore } from '@ariakit/react';

interface Props {
  navStartItems?: React.ReactNode[];
  navEndItems?: React.ReactNode[];
}

export default function ResponsiveNavigationLayout({
  children,
  navStartItems,
  navEndItems,
}: PropsWithChildren<Props>) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const viewportWidthClassType = useViewportWidthClassType();
  const didPathnameChange = useDidChange(usePathname());
  const didViewportWidthClassTypeChange = useDidChange(viewportWidthClassType);
  const dialogStore = useDialogStore({
    open: !isCollapsed,
    setOpen: (open) => setIsCollapsed(!open),
  });

  if (didPathnameChange || didViewportWidthClassTypeChange) {
    setIsCollapsed(true);
  }

  const menuIcon = isCollapsed ? (
    <DallasSvgIconMenuOutlined />
  ) : (
    <DallasSvgIconMenuOpenOutlined />
  );
  const menuAriaLabel = isCollapsed
    ? 'Expand navigation'
    : 'Collapse navigation';
  const navContent = (
    <DallasStack
      className={clsx(styles.navItemsStack, styles.wide)}
      paddingVertical="lg"
      paddingHorizontal="md"
      alignVertical="space-between"
    >
      <DallasStack gap="xl" alignHorizontal="center">
        <Link className={styles.tsWordmarkWrapper} href="/" prefetch={false}>
          <DallasImageTruckSmarterLogo
            variant="wordmark"
            color="brand"
            width={178}
          />
        </Link>
        <DallasStack gap="2xs">{navStartItems}</DallasStack>
      </DallasStack>
      {navEndItems != null && (
        <DallasStack gap="2xs">{navEndItems}</DallasStack>
      )}
    </DallasStack>
  );

  return (
    <DallasStack
      className={styles.root}
      gap="none"
      direction="row"
      compact-direction="column"
    >
      {viewportWidthClassType === 'extended' ? (
        <nav className={styles.standAloneNav}>{navContent}</nav>
      ) : (
        <>
          <BaseOverlay
            className={styles.navOverlay}
            store={dialogStore}
            autoFocusOnShow={true}
            autoFocusOnHide={false}
            portal={false}
          >
            {navContent}
          </BaseOverlay>
          <DallasStack
            as="nav"
            className={clsx(
              styles.navItemsStack,
              styles.narrow,
              isCollapsed && styles.collapsed,
            )}
            paddingVertical="lg"
            paddingHorizontal="xs"
            compact-direction="row"
            compact-paddingVertical="xs"
            compact-alignVertical="center"
          >
            <Link
              className={styles.tsMonogramWrapper}
              href="/"
              prefetch={false}
            >
              <DallasImageTruckSmarterLogo
                variant="monogram"
                color="brand"
                width={viewportWidthClassType === 'compact' ? 32 : 40}
              />
            </Link>
            <NavIconButton
              icon={menuIcon}
              aria-label={menuAriaLabel}
              onClick={() => setIsCollapsed((value) => !value)}
            />
          </DallasStack>
        </>
      )}
      <DallasStack
        className={styles.content}
        direction="column"
        gap="none"
        flex
      >
        {children}
      </DallasStack>
    </DallasStack>
  );
}
